"use client"

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {ChevronRight} from "lucide-react";
import Link from "next/link";
import {cn} from "@/lib/utils";
import {useUserRoles} from "@/hooks/use-user-roles";
import {usePathname} from "next/navigation";
import {gaId} from "@/lib/analytics/clickTracker";
import {useCompanyFeatureFlag} from "@/hooks/queries/use-feature-flags";

export interface NavAgentItem {
  title: string
  url: string
  activeCheck: (url: string) => boolean
  icon?: React.ElementType | null
  className?: string
  adminOnly?: boolean
}

export interface NavAgentGroup {
  title: string
  icon?: React.ElementType | null
  items: NavAgentItem[]
}

export type NavAgentEntry = NavAgentItem | NavAgentGroup

function isNavAgentGroup(entry: NavAgentEntry): entry is NavAgentGroup {
  return "items" in entry
}

const subButtonCollapsedClass =
  "group-data-[collapsible=icon]:!flex group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!p-2 group-data-[collapsible=icon]:[&>span:last-child]:!hidden"

function NavAgentGroupRow({group, items, pathname}: {group: NavAgentGroup, items: NavAgentItem[], pathname: string}) {
  return (
    <SidebarMenuItem className="pt-[5px]" {...gaId(group.title, "sidebar_nav_click")}>
      <SidebarMenuButton
        className="w-full cursor-default text-xs group-data-[collapsible=icon]:!hidden"
        isActive={false}
        tooltip={group.title}
        {...gaId(group.title, "sidebar_nav_click")}
      >
        {group.icon && <group.icon/>}
        <span className={cn("truncate flex-1 h-auto w-auto uppercase")}>{group.title}</span>
      </SidebarMenuButton>
      <SidebarMenuSub
        className="!mx-0 !px-0 group-data-[collapsible=icon]:!flex group-data-[collapsible=icon]:!flex-col group-data-[collapsible=icon]:!items-center"
      >
        {items.map((item) => (
          <SidebarMenuSubItem key={`${group.title}-${item.title}`}>
            <SidebarMenuSubButton
              asChild
              isActive={item.activeCheck(pathname)}
              tooltip={item.title}
              className={subButtonCollapsedClass}
              {...gaId(item.title, "sidebar_nav_click")}
            >
              <Link href={item.url}>
                {item.icon && <item.icon/>}
                <span className={cn("truncate flex-1 h-auto w-auto")}>
                  {item.title}
                  {item.adminOnly && <span className="text-xs"> (Admin)</span>}
                </span>
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        ))}
      </SidebarMenuSub>
    </SidebarMenuItem>
  )
}

export function NavAgent({items, subContent}: { items: NavAgentEntry[]; subContent?: Record<string, React.ReactNode> }) {
  const {admin} = useUserRoles()
  const showSignals = useCompanyFeatureFlag(ff => ff.wsSignals)
  const showInsights = useCompanyFeatureFlag(ff => ff.wsInsights)
  const pathname = usePathname();

  const isVisible = (item: NavAgentItem) => {
    if (item.adminOnly && !admin) {
      return false
    }
    if (item.title === 'Signals' && !showSignals) {
      return false
    }
    if (item.title === 'Insights' && !showInsights) {
      return false
    }
    return true
  }

  return (
    <SidebarGroup className="px-2 py-0">
      <SidebarMenu>
        {items.map((entry) => {
          if (isNavAgentGroup(entry)) {
            const visibleItems = entry.items.filter(isVisible)
            if (!visibleItems.length) {
              return null
            }
            return (
              <NavAgentGroupRow
                key={entry.title}
                group={entry}
                items={visibleItems}
                pathname={pathname}
              />
            )
          }

          if (!isVisible(entry)) {
            return null
          }

          const nestedContent = subContent?.[entry.title]
          if (nestedContent) {
            return (
              <Collapsible key={entry.title} asChild className="group/collapsible">
                <SidebarMenuItem {...gaId(entry.title, "sidebar_nav_click")}>
                  <SidebarMenuButton
                    asChild
                    tooltip={entry.title}
                    className="w-full"
                    isActive={entry.activeCheck(pathname)}
                    {...gaId(entry.title, "sidebar_nav_click")}
                  >
                    <Link href={entry.url}>
                      {entry.icon && <entry.icon/>}
                      <span className={cn("truncate flex-1 h-auto w-auto")}>{entry.title}</span>
                    </Link>
                  </SidebarMenuButton>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuAction aria-label={`Toggle ${entry.title}`}>
                      <ChevronRight className="transition-transform group-data-[state=open]/collapsible:rotate-90"/>
                    </SidebarMenuAction>
                  </CollapsibleTrigger>
                  <CollapsibleContent>{nestedContent}</CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )
          }

          return (
            <SidebarMenuItem key={entry.title} {...gaId(entry.title, "sidebar_nav_click")}>
              <SidebarMenuButton
                asChild
                tooltip={entry.title}
                className="w-full"
                isActive={entry.activeCheck(pathname)}
                {...gaId(entry.title, "sidebar_nav_click")}
              >
                <Link href={entry.url}>
                  {entry.icon && <entry.icon/>}
                  <span className={cn("truncate flex-1 h-auto w-auto")}>
                    {entry.title}
                    {entry.adminOnly && <span className="text-xs"> (Admin)</span>}
                  </span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}

export function NavAgentItem({title, url, icon: Icon, className}: NavAgentItem) {
  return (
    <Link
      href={url}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-gray-500 transition-all hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-50",
        className
      )}
    >
      {Icon && <Icon className="h-4 w-4"/>}
      <span>{title}</span>
    </Link>
  )
}
